import { useState, useMemo, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useGetNotifications, useMarkNotificationAsRead, useMarkAllNotificationsAsRead } from '@/services/notifications/queries';
import { useAuthStore } from '@/store/auth';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { NotificationAudience } from '@/services/types';
import { usePostHog } from 'posthog-js/react';
 

export function NotificationsDropdown() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const posthog = usePostHog();
  const { user, context } = useAuthStore();

  // Determine available tabs based on user context
  const availableTabs = useMemo(() => {
    type TabType = 'personal' | 'workspace' | 'organisation';
    const tabs: TabType[] = ['personal'];
    
    if (context === 'workspace') {
      tabs.push('workspace');
    }
    if (context === 'organisation') {
      tabs.push('organisation');
    }
    
    return tabs;
  }, [context]);

  const [activeTab, setActiveTab] = useState<'personal' | 'workspace' | 'organisation'>(
    availableTabs[0] || 'personal'
  );

  useEffect(() => {
    if (!availableTabs.includes(activeTab)) {
      setActiveTab(availableTabs[0] || 'personal');
    }
  }, [availableTabs, activeTab]);

  // Per-tab queries (audience)
  const { data: myData, isLoading: loadingMy, refetch: refetchMy } = useGetNotifications(
    { limit: 10, audience: NotificationAudience.USER },
    { 
      enabled: !!user?.id,
      refetchOnWindowFocus: true, 
      staleTime: 1000 * 60 * 3 // 3 minutes
    }
  );

  const { data: workspaceData, isLoading: loadingWorkspace, refetch: refetchWorkspace } = useGetNotifications(
    { limit: 10, audience: NotificationAudience.WORKSPACE },
    {
      enabled: context === 'workspace', 
      refetchOnWindowFocus: true, 
      staleTime: 1000 * 60 * 3 // 3 minutes
    }
  );

  const { data: organisationData, isLoading: loadingOrganisation, refetch: refetchOrganisation } = useGetNotifications(
    { limit: 10, audience: NotificationAudience.ORGANISATION },
    { 
      enabled: context === 'organisation', 
      refetchOnWindowFocus: true, 
      staleTime: 1000 * 60 * 3 // 3 minutes
    }
  );

  const isLoading = (activeTab === 'personal' && loadingMy)
    || (activeTab === 'workspace' && loadingWorkspace)
    || (activeTab === 'organisation' && loadingOrganisation);

  // Minimal helpers based on shape: { id, read, notification: { title, createdAt, url } }
  const isUnread = (n: any) => !n?.read;

  const myList = myData?.data || [];
  const workspaceList = workspaceData?.data || [];
  const organisationList = organisationData?.data || [];

  const notifications = activeTab === 'personal' ? myList : activeTab === 'workspace' ? workspaceList : organisationList;
  
  // Unread counts per tab and total
  const myUnreadCount = useMemo(() => myList.filter(isUnread).length, [myList]);
  const workspaceUnreadCount = useMemo(() => workspaceList.filter(isUnread).length, [workspaceList]);
  const organisationUnreadCount = useMemo(() => organisationList.filter(isUnread).length, [organisationList]);
  const unreadCount = useMemo(() => {
    let total = myUnreadCount;
    if (context === 'workspace') total += workspaceUnreadCount;
    if (context === 'organisation') total += organisationUnreadCount;
    return total;
  }, [myUnreadCount, workspaceUnreadCount, organisationUnreadCount, context]);

  const { mutate: markAsRead } = useMarkNotificationAsRead(
    () => {
      refetchMy();
      if (context === 'workspace') refetchWorkspace();
      if (context === 'organisation') refetchOrganisation();
    },
    (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to mark notification as read');
    }
  );

  const { mutate: markAllAsRead } = useMarkAllNotificationsAsRead(
    () => {
      refetchMy();
      if (context === 'workspace') refetchWorkspace();
      if (context === 'organisation') refetchOrganisation();
      toast.success('All notifications marked as read');
    },
    (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to mark all as read');
    }
  );

  const handleNotificationClick = (status: any) => {
    const isUnreadNotif = isUnread(status);
    posthog?.capture('notification_clicked', {
      notification_id: status?.id,
      notification_title: status?.notification?.title,
      was_unread: isUnreadNotif,
      tab: activeTab,
    });
    if (isUnreadNotif) {
      if (status?.id) markAsRead([status.id]);
    }
    const url = status?.notification?.url as string | undefined;
    if (url) {
      navigate(url);
      setOpen(false);
    }
  };

  const formatDate = (date: Date | string) => {
    const now = new Date();
    const notificationDate = new Date(date);
    const diffInSeconds = Math.floor((now.getTime() - notificationDate.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}m ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours}h ago`;
    } else if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days}d ago`;
    } else {
      return notificationDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: now.getFullYear() !== notificationDate.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  if (!user) return null;

  return (
    <DropdownMenu open={open} onOpenChange={(isOpen) => {
      if (isOpen) posthog?.capture('modal_opened', { modal_name: 'notifications_dropdown', unread_count: unreadCount });
      setOpen(isOpen);
    }}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span
              className="absolute -top-2 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 border-2 border-white text-white text-[10px] leading-[14px] flex items-center justify-center shadow-sm"
              aria-label={`${unreadCount} unread notifications`}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
          <span className="sr-only">Notifications</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-[400px] p-0 bg-white border shadow-lg rounded-lg"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div className="border-b border-gray-100 px-5 py-4 bg-white">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-base text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-auto py-1.5 px-3 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                onClick={() => markAllAsRead()}
                trackingEvent='notifications_mark_all_read'
                trackingProperties={{ unread_count: unreadCount, tab: activeTab }}
              >
                Mark all as read
              </Button>
            )}
          </div>
        </div>

        <Tabs
          value={activeTab}
          onValueChange={(v) => {
            posthog?.capture('tab_switched', { from: activeTab, to: v, location: 'notifications_dropdown' });
            setActiveTab(v as any);
          }}
          className="w-full bg-white"
        >
          {availableTabs.length > 1 && (
            <TabsList className="w-full rounded-none border-b border-gray-100 h-auto px-5 bg-white">
              {availableTabs.map((tab) => {
                const label = tab === 'personal' ? 'Personal' : tab.charAt(0).toUpperCase() + tab.slice(1);
                const dot = tab === 'personal' ? myUnreadCount > 0 : tab === 'workspace' ? workspaceUnreadCount > 0 : organisationUnreadCount > 0;
                return (
                  <TabsTrigger
                    key={tab}
                    value={tab}
                    className="capitalize px-4 py-2.5 text-sm font-medium data-[state=active]:text-gray-900 data-[state=active]:border-b-2 data-[state=active]:border-gray-900 data-[state=inactive]:text-gray-500 hover:text-gray-700"
                  >
                    <span className="inline-flex items-center gap-2">
                      {label}
                      {dot && <span className="h-2 w-2 rounded-full bg-red-500 relative -top-1 shadow-sm" />}
                    </span>
                  </TabsTrigger>
                );
              })}
            </TabsList>
          )}

          {availableTabs.map((tab) => (
            <TabsContent key={tab} value={tab} className="m-0 p-0">
              <ScrollArea className="h-[450px] bg-white">
                {isLoading ? (
                  <div className="flex items-center justify-center py-12 bg-white">
                    <p className="text-sm text-gray-500">Loading notifications...</p>
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 px-4 text-center bg-white">
                    <Bell className="h-10 w-10 text-gray-300 mb-3" />
                    <p className="text-sm font-medium text-gray-900">No notifications</p>
                    <p className="text-xs text-gray-500 mt-1.5">
                      {tab === 'personal' 
                        ? "You're all caught up!"
                        : `No ${tab} notifications yet`}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100 bg-white">
                    {notifications.map((status: any) => {
                      const unread = isUnread(status);
                      return (
                        <div
                          key={status?.id}
                          className={cn(
                            "px-5 py-3.5 cursor-pointer transition-colors bg-white",
                            unread ? "bg-blue-50 border-l-[3px] border-l-blue-500" : "hover:bg-gray-50"
                          )}
                          onClick={() => handleNotificationClick(status)}
                        >
                          <div className="flex items-start gap-3">
                            <div className={cn(
                              "mt-1.5 h-2 w-2 rounded-full flex-shrink-0",
                              unread ? "bg-blue-500" : "bg-transparent"
                            )} />
                            <div className="flex-1 min-w-0">
                              <p className={cn(
                                "text-sm leading-5 truncate",
                                unread ? "font-semibold text-gray-900" : "font-medium text-gray-700"
                              )}>
                                {status?.notification?.title || 'Notification'}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {formatDate(status?.notification?.createdAt)}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>
          ))}
        </Tabs>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

